import { useNotificationsDashboard } from '@/features/notifications/notifications.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { formatDistanceToNow } from 'date-fns';
import { el } from 'date-fns/locale';
import { Loader2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import NotificationCreateSheet from '@/components/sheets/notification-create-sheet/notification-create-sheet';
import NotificationSheet from '@/components/sheets/notification-sheet/notification-sheet';

const notificationsRouteSchema = z.object({
  mode: z.enum(['view', 'create']).optional(),
  notification: z.number().optional(),
});

export const Route = createFileRoute('/_app/(notifications)/notifications/')({
  validateSearch: zodSearchValidator(notificationsRouteSchema),
  component: NotificationsPage,
});

function NotificationsPage() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <NotificationsTabHeader />
      <NotificationsTable />
      <NotificationCreateSheet />
      <NotificationSheet />
    </div>
  );
}

function NotificationsTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['notifications', 'common', 'sidebar']);

  const createNotification = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createNotification} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {toCapitalize(t('notifications', { ns: 'sidebar' }))}
        </Button>
      </div>
    </div>
  );
}

function NotificationsTable() {
  const { t } = useTranslation(['table', 'common']);

  const { data = [], isLoading } = useNotificationsDashboard();
  return (
    <>
      <DataTableSimple
        data={data}
        columns={['id', 'title', 'channel', 'sentAt', 'recipientsCount', 'totalRecipients']}
        isLoading={isLoading}
        columnsLabels={{
          id: t('serial_id'),
          title: toCapitalize(t('name')),
          channel: 'Κανάλια',
          sentAt: 'Αποστολή',
          recipientsCount: 'Παραλήπτες',
          totalRecipients: 'Κατάσταση',
        }}
        customRender={{
          title: (row) => (
            <Link to={'.'} search={{ notification: row.original.id, mode: 'view' }}>
              {row.original.title}
            </Link>
          ),
          sentAt: (row) =>
            formatDistanceToNow(new Date(row.original.sentAt), {
              addSuffix: true,
              locale: el,
            }),
          channel: (row) => {
            return (
              <div className='flex gap-2'>
                {row.original.channel === 'Email' && (
                  <span className='rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700'>
                    📧 Email
                  </span>
                )}

                {row.original.channel === 'Push' && (
                  <span className='rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700'>
                    🔔 Push
                  </span>
                )}
              </div>
            );
          },
          recipientsCount: (row) => (
            <span className='font-medium'>{row.original.totalRecipients}</span>
          ),
          totalRecipients: (row) => {
            return row.original.status === 'Processing' ? (
              <div className='flex items-center justify-center'>
                <Loader2 className='h-5 w-5 animate-spin text-gray-500' />
              </div>
            ) : (
              <NotificationStatusBarColumn
                total={row.original.totalRecipients}
                read={row.original.readCount}
                readPercentage={row.original.readPercentage}
                delivered={row.original.deliveredCount}
                requiresAction={row.original.requiresAction}
              />
            );
          },
        }}
        extraColumns={[]}
        className='flex h-full flex-col overflow-y-auto'
      />
    </>
  );
}

function NotificationStatusBarColumn({
  total,
  read,
  readPercentage,
  delivered,
  requiresAction,
}: {
  total: number;
  read: number;
  readPercentage: number;
  delivered: number;
  requiresAction: boolean;
}) {
  const failed = total - delivered;

  // For requiresAction:
  const pending = requiresAction ? delivered - read : 0;

  const pct = (count: number) => (total === 0 ? 0 : (count / total) * 100);

  return (
    <div className='flex w-full flex-col gap-1'>
      <div className='flex h-3 w-full overflow-hidden rounded bg-gray-200'>
        {/* READ (only if requiresAction) */}
        {requiresAction && <div className='bg-green-500' style={{ width: `${readPercentage}%` }} />}

        {/* PENDING (requiresAction) */}
        {requiresAction && pending > 0 && (
          <div className='bg-gray-400' style={{ width: `${pct(pending)}%` }} />
        )}

        {/* DELIVERED (non requiresAction) */}
        {!requiresAction && delivered > 0 && (
          <div className='bg-blue-500' style={{ width: `${pct(delivered)}%` }} />
        )}

        {/* FAILED */}
        {failed > 0 && <div className='bg-red-500' style={{ width: `${pct(failed)}%` }} />}
      </div>

      {/* LEGEND */}
      <div className='flex items-center gap-3 text-xs'>
        {requiresAction && (
          <>
            <span className='flex items-center gap-1 text-green-600'>
              <span className='h-2 w-2 rounded-full bg-green-500' />
              {read} Διαβάστηκαν
            </span>

            <span className='flex items-center gap-1 text-gray-600'>
              <span className='h-2 w-2 rounded-full bg-gray-400' />
              {pending} Εκκρεμή
            </span>
          </>
        )}

        {!requiresAction && (
          <span className='flex items-center gap-1 text-blue-600'>
            <span className='h-2 w-2 rounded-full bg-blue-500' />
            {delivered} Παραδόθηκαν
          </span>
        )}

        <span className='flex items-center gap-1 text-red-600'>
          <span className='h-2 w-2 rounded-full bg-red-500' />
          {failed} Απέτυχαν
        </span>
      </div>
    </div>
  );
}
